import type { ByteSource } from './bytes';
import { MpqArchive } from './mpq/archive';
import { normalizePath } from './mpq/crypto';

/**
 * Blizzard's BNUpdate patches (`LOD_109D.EXE`, `D2PATCH1.EXE`, `D2Patch_114d.exe`): a
 * self-extracting executable with an MPQ appended. The MPQ holds
 *
 *  - `patch.lst`: `full\path;shortName;0x0` lines, one per patched file (the short name is the
 *    file's name inside the MPQ), ended by `*`;
 *  - `patch.cmd`: the patcher script (`PatchVersion <text>`, version checks);
 *  - `patch.txt`: the version history shown to the player (`- Patch 1.09d` headings);
 *  - one entry per patched file: a 24-byte header, then either the whole new file or a delta
 *    against the previous version.
 *
 * Header: `u16 0x18, u8 4, u8 kind (1 = full, 0 = delta), u32 crc32(old file), u32 oldSize,
 * u32 newSize, FILETIME`. A delta body is `u32 len1, u32 len2`, then two sections:
 *
 *  1. **Commands** (`len1` bytes) build the new file from left to right while a cursor walks the
 *     old file. Each command starts with a little-endian u16: the top two bits are the op, the low
 *     14 bits a byte count `n`. Every op advances the old-file cursor by `n` as well.
 *     - op 0 `insert`: `n` literal bytes follow (they stand in for `n` old bytes).
 *     - op 1 `copy`: a signed varint follows; the cursor moves by it, then `n` old bytes are copied.
 *     - op 2 `shifted copy`: like copy, but every 16-bit word is moved by the amount the word
 *       written just before it moved (new word − old word at cursor − 2). Relocation tables and
 *       sorted offset/id columns, which all shift by a constant after an insertion, cost two bytes.
 *     - op 3 `zero`: `n` zero bytes.
 *  2. **Fix-ups** (`len2` bytes), applied to the result: 16-bit little-endian additions grouped by
 *     addend. Each group is `varint delta, varint position, varint gap…, 0`; the addend is the
 *     previous group's plus `delta` (signed for the first group, unsigned after: groups are sorted),
 *     positions are absolute then relative to the previous one. A 0 byte ends the section. This
 *     captures edits like "every row's column 12 goes up by one" in a few bytes.
 *
 * Varints are prefix-coded, low bits first: `0xxxxxxx` (7 bits), `10xxxxxx b` (14),
 * `110xxxxx b b` (21), `111xxxxx b b b` (29). Signed values are sign-extended from that width.
 *
 * Verified byte-exact against the full copies of the same files in the other 1.09d patch, and on
 * executables against PE checksums and relocation tables (docs/FORMATS.md).
 *
 * A delta names its base only by size and CRC-32. The 1.09d patches' bases are the files the
 * archives serve; 1.14d ("from version 1.00 or later") diffs most files against the classic
 * 1.00 copy in `d2data.mpq` that `d2exp.mpq` shadows, so the caller looks the base up by its
 * header (see `ArchiveSet`).
 */

export class PatchError extends Error {}

export const PatchKind = { Delta: 0, Full: 1 } as const;
export type PatchKind = (typeof PatchKind)[keyof typeof PatchKind];

export interface PatchHeader {
  kind: PatchKind;
  /** CRC-32 of the file this delta applies to (0 for full files). */
  oldCrc: number;
  oldSize: number;
  newSize: number;
}

export const PATCH_HEADER_SIZE = 24;

export function readPatchHeader(file: Uint8Array): PatchHeader {
  if (file.length < PATCH_HEADER_SIZE) throw new PatchError('patch entry: truncated header');
  const dv = new DataView(file.buffer, file.byteOffset, file.byteLength);
  if (dv.getUint16(0, true) !== PATCH_HEADER_SIZE || file[2] !== 4) throw new PatchError('patch entry: bad header');
  const kind = file[3] as PatchKind;
  if (kind !== PatchKind.Delta && kind !== PatchKind.Full) throw new PatchError(`patch entry: unknown kind ${file[3]}`);
  return { kind, oldCrc: dv.getUint32(4, true), oldSize: dv.getUint32(8, true), newSize: dv.getUint32(12, true) };
}

/**
 * Produces the new file from a patch entry. `old` is required for deltas and must be the exact
 * file the delta was made against (checked by size and CRC-32).
 */
export function applyPatchFile(file: Uint8Array, old: Uint8Array | null): Uint8Array {
  const h = readPatchHeader(file);
  const body = file.subarray(PATCH_HEADER_SIZE);
  if (h.kind === PatchKind.Full) {
    if (body.length < h.newSize) throw new PatchError('patch entry: truncated file');
    return body.slice(0, h.newSize);
  }
  if (!old) throw new PatchError('delta needs the previous version of the file');
  if (!matchesBase(old, h)) throw new PatchError('delta does not apply to this version of the file');
  if (body.length < 8) throw new PatchError('delta: truncated');
  const dv = new DataView(body.buffer, body.byteOffset, body.byteLength);
  const len1 = dv.getUint32(0, true);
  const len2 = dv.getUint32(4, true);
  if (8 + len1 + len2 > body.length) throw new PatchError('delta: sections overrun the entry');
  return applyDelta(old, body.subarray(8, 8 + len1), body.subarray(8 + len1, 8 + len1 + len2), h.newSize);
}

/** Runs a delta's command and fix-up sections (see the module comment). */
export function applyDelta(old: Uint8Array, commands: Uint8Array, fixups: Uint8Array, newSize: number): Uint8Array {
  const out = new Uint8Array(newSize);
  const cur = { i: 0 };
  let o = 0;
  let src = 0;
  while (cur.i < commands.length) {
    if (cur.i + 2 > commands.length) throw new PatchError('delta: truncated command');
    const h = commands[cur.i] | (commands[cur.i + 1] << 8);
    cur.i += 2;
    const n = h & 0x3fff;
    if (o + n > newSize) throw new PatchError('delta: output overrun');
    switch (h >> 14) {
      case 0:
        if (cur.i + n > commands.length) throw new PatchError('delta: truncated literal');
        out.set(commands.subarray(cur.i, cur.i + n), o);
        cur.i += n;
        src += n;
        break;
      case 1:
        src += readVarint(commands, cur, true);
        if (src < 0 || src + n > old.length) throw new PatchError('delta: copy outside the old file');
        out.set(old.subarray(src, src + n), o);
        src += n;
        break;
      case 2: {
        src += readVarint(commands, cur, true);
        if (o < 2 || src < 2 || src + n > old.length) throw new PatchError('delta: shifted copy outside the file');
        // Every 16-bit word moves by the amount the word just written moved.
        const shift = (out[o - 2] | (out[o - 1] << 8)) - (old[src - 2] | (old[src - 1] << 8));
        let k = 0;
        for (; k + 1 < n; k += 2) {
          const w = ((old[src + k] | (old[src + k + 1] << 8)) + shift) & 0xffff;
          out[o + k] = w & 0xff;
          out[o + k + 1] = w >> 8;
        }
        if (k < n) out[o + k] = old[src + k];
        src += n;
        break;
      }
      case 3:
        src += n; // the output is already zeroed
        break;
    }
    o += n;
  }
  if (o !== newSize) throw new PatchError(`delta: produced ${o} bytes, expected ${newSize}`);

  cur.i = 0;
  let addend = 0;
  let first = true;
  while (cur.i < fixups.length && fixups[cur.i] !== 0) {
    addend = (addend + readVarint(fixups, cur, first)) | 0;
    first = false;
    let pos = readVarint(fixups, cur, false);
    for (;;) {
      add16(out, pos, addend);
      if (cur.i >= fixups.length) throw new PatchError('delta: truncated fix-up');
      if (fixups[cur.i] === 0) {
        cur.i++;
        break;
      }
      pos += readVarint(fixups, cur, false);
    }
  }
  return out;
}

/** Adds `v` (mod 2^16) to the little-endian u16 at `pos`; at the last byte only the low half exists. */
function add16(b: Uint8Array, pos: number, v: number): void {
  if (pos < 0 || pos >= b.length) throw new PatchError('delta: fix-up outside the file');
  const w = b[pos] + (pos + 1 < b.length ? b[pos + 1] << 8 : 0) + v;
  b[pos] = w & 0xff;
  if (pos + 1 < b.length) b[pos + 1] = (w >> 8) & 0xff;
}

/** Prefix-coded varint (see the module comment). */
export function readVarint(b: Uint8Array, cur: { i: number }, signed: boolean): number {
  if (cur.i >= b.length) throw new PatchError('delta: truncated number');
  const c = b[cur.i++];
  const extra = c < 0x80 ? 0 : c < 0xc0 ? 1 : c < 0xe0 ? 2 : 3;
  if (cur.i + extra > b.length) throw new PatchError('delta: truncated number');
  const lowBits = extra === 3 ? 5 : 7 - extra;
  let v = c & ((1 << lowBits) - 1);
  for (let k = 0; k < extra; k++) v += b[cur.i++] * 2 ** (lowBits + 8 * k);
  const bits = lowBits + 8 * extra;
  if (signed && v >= 2 ** (bits - 1)) v -= 2 ** bits;
  return v;
}

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

/** Standard CRC-32 (IEEE 802.3, as zlib computes it). */
export function crc32(data: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < data.length; i++) c = CRC_TABLE[(c ^ data[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/**
 * A BNUpdate patch executable (or its MPQ) as a read-only layer of patched files. Entries are
 * decoded on demand; the caller supplies the previous version of a file for deltas.
 */
export class PatchArchive {
  private constructor(
    readonly mpq: MpqArchive,
    /** Normalized full path → path as listed and name inside the MPQ. */
    private readonly entries: Map<string, { path: string; short: string }>,
    /** Patcher script text (`patch.cmd`). */
    readonly script: string,
    /** Version the patch brings the game to (e.g. "1.09d"), or null if not stated. */
    readonly version: string | null,
  ) {}

  /** Opens a patch from an executable (the MPQ is found by scanning) or a bare MPQ. */
  static async open(src: ByteSource, name = 'patch'): Promise<PatchArchive> {
    const mpq = await MpqArchive.open(src, name, true);
    const list = await mpq.tryRead('patch.lst');
    if (!list) throw new PatchError(`${name}: not a Blizzard patch (no patch.lst)`);
    const entries = new Map<string, { path: string; short: string }>();
    for (const line of latin1(list).split(/\r?\n/)) {
      const [full, short] = line.split(';').map((x) => x.trim());
      if (full && short && full !== '*') entries.set(normalizePath(full), { path: full, short });
    }
    const script = latin1((await mpq.tryRead('patch.cmd')) ?? new Uint8Array(0));
    const history = await mpq.tryRead('patch.txt');
    return new PatchArchive(mpq, entries, script, patchVersion(history ? latin1(history.subarray(0, 4096)) : '', script));
  }

  /**
   * True if the patch upgrades Lord of Destruction. Up to 1.13 Blizzard shipped separate classic
   * (`D2Patch_…`) and expansion (`LODPatch_…`) patches and the script names the expansion; 1.14's
   * single installer ("upgrades Diablo II from version 1.00 or later") patches both.
   */
  get forExpansion(): boolean {
    return /^PatchVersion .*Lord of Destruction/im.test(this.script) || compareVersions(this.version ?? '0', '1.14') >= 0;
  }

  /** Full paths of every patched file (as written in patch.lst). */
  paths(): string[] {
    return [...this.entries.values()].map((e) => e.path);
  }

  has(path: string): boolean {
    return this.entries.has(normalizePath(path));
  }

  /** The raw patch entry (header + body) for `path`, or null. */
  async entry(path: string): Promise<Uint8Array | null> {
    const e = this.entries.get(normalizePath(path));
    return e ? this.mpq.tryRead(e.short) : null;
  }

  /**
   * True if the patch's deltas match the data `base` serves: the first game-data delta whose
   * base exists is checked by size and CRC-32 (one small file is read).
   */
  async appliesTo(base: DeltaBase): Promise<boolean> {
    for (const path of this.paths()) {
      if (!/^data[\\/]/i.test(path)) continue;
      const file = await this.entry(path);
      if (!file) continue;
      const h = readPatchHeader(file);
      if (h.kind !== PatchKind.Delta) continue;
      const old = await base(path, h);
      if (!old) continue;
      return matchesBase(old, h);
    }
    return true;
  }

  /**
   * The patched version of `path` (null if the patch doesn't touch it). `base` reads the
   * unpatched file the delta was made against; it is only called for deltas.
   */
  async read(path: string, base: DeltaBase): Promise<Uint8Array | null> {
    const file = await this.entry(path);
    if (!file) return null;
    const h = readPatchHeader(file);
    return applyPatchFile(file, h.kind === PatchKind.Delta ? await base(path, h) : null);
  }
}

/** Reads the copy of `path` a delta was made against (null if no archive holds the file). */
export type DeltaBase = (path: string, header: PatchHeader) => Promise<Uint8Array | null>;

/** "1.09d" from the history's first "- Patch x.yyz" heading, else "1.09" from the script. */
function patchVersion(history: string, script: string): string | null {
  const h = /^-\s*Patch\s+(\d+\.\d+[a-z]?)\b/im.exec(history);
  if (h) return h[1];
  const s = /^PatchVersion .*\bto version (\d+\.\d+[a-z]?)/im.exec(script);
  return s ? s[1] : null;
}

/** True if `old` is the file a delta with header `h` was made against (size and CRC-32). */
export function matchesBase(old: Uint8Array, h: PatchHeader): boolean {
  return old.length === h.oldSize && crc32(old) === h.oldCrc;
}

/** Orders game versions ("1.09d" < "1.10" < "1.14d"): numeric part, then the letter. */
export function compareVersions(a: string, b: string): number {
  const parse = (v: string) => {
    const m = /^(\d+)\.(\d+)([a-z]?)/i.exec(v);
    return m ? [Number(m[1]), Number(m[2]), m[3] ? m[3].toLowerCase().charCodeAt(0) - 96 : 0] : [0, 0, 0];
  };
  const x = parse(a), y = parse(b);
  for (let i = 0; i < 3; i++) if (x[i] !== y[i]) return x[i] - y[i];
  return 0;
}

function latin1(b: Uint8Array): string {
  let s = '';
  for (let i = 0; i < b.length; i += 0x8000) s += String.fromCharCode(...b.subarray(i, i + 0x8000));
  return s;
}
